import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BookMarked,
  CheckCircle2,
  ClipboardList,
  FileQuestion,
  FileText,
  GraduationCap,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  LmsAdminDashboardActivity,
  LmsCourseStatus,
  LmsDashboardApi,
  LmsTeacherDashboard,
  LmsTeacherDashboardCourse,
  LmsTeacherDashboardRecentSubmission,
} from '@/services/lms.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { formatDateTime, formatRelative } from '@/lib/format';
import { usePermissions } from '@/hooks/usePermissions';

const statusTone: Record<LmsCourseStatus, 'slate' | 'green' | 'amber'> = {
  draft: 'slate',
  published: 'green',
  archived: 'amber',
};

export function LmsDashboardPage() {
  const { isAdmin } = usePermissions();

  return (
    <>
      <PageHeader title="LMS Dashboard" description="Courses, grading, and activity across the LMS." />
      {isAdmin ? <AdminDashboard /> : <TeacherDashboard />}
    </>
  );
}

function StatCard({
  to,
  icon: Icon,
  label,
  value,
  subline,
  tone,
  loading,
}: {
  to: string;
  icon: typeof Users;
  label: string;
  value: number;
  subline?: string;
  tone: 'blue' | 'indigo' | 'purple' | 'amber' | 'green';
  loading?: boolean;
}) {
  const toneClass = {
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
  }[tone];

  return (
    <Link to={to} className="card group block p-4 transition hover:border-slate-300 hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">
            {loading ? <span className="text-slate-300">—</span> : value}
          </div>
          {subline && <div className="mt-0.5 text-[10px] text-slate-500">{subline}</div>}
        </div>
        <div className={`rounded-md p-1.5 ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

function MyCoursesTable({ courses, isLoading }: { courses: LmsTeacherDashboardCourse[]; isLoading: boolean }) {
  return (
    <DataTable<LmsTeacherDashboardCourse>
      rows={courses}
      getRowId={(c) => c.id}
      isLoading={isLoading}
      emptyMessage="No courses yet."
      columns={[
        {
          key: 'title',
          header: 'Title',
          render: (c) => (
            <Link to={`/lms/courses/${c.id}`} className="font-medium text-slate-900 hover:text-brand-700 hover:underline">
              {c.title}
            </Link>
          ),
        },
        { key: 'status', header: 'Status', render: (c) => <Badge tone={statusTone[c.status]}>{c.status}</Badge> },
        { key: 'enrolledCount', header: 'Enrolled', render: (c) => c.enrolledCount },
        { key: 'avgProgress', header: 'Avg. Progress', render: (c) => `${c.avgProgress}%` },
      ]}
    />
  );
}

function RecentSubmissionsList({
  items,
  isLoading,
}: {
  items: LmsTeacherDashboardRecentSubmission[];
  isLoading: boolean;
}) {
  return (
    <DataTable<LmsTeacherDashboardRecentSubmission>
      rows={items}
      getRowId={(s) => `${s.assignmentTitle}-${s.studentName}-${s.submittedAt}`}
      isLoading={isLoading}
      emptyMessage="No submissions yet."
      columns={[
        { key: 'assignmentTitle', header: 'Assignment', render: (s) => s.assignmentTitle ?? '—' },
        { key: 'studentName', header: 'Student', render: (s) => s.studentName ?? '—' },
        {
          key: 'submittedAt',
          header: 'Submitted',
          render: (s) => <span title={formatDateTime(s.submittedAt)}>{formatRelative(s.submittedAt)}</span>,
        },
        {
          key: 'isLate',
          header: 'Late',
          render: (s) => (s.isLate ? <Badge tone="red">Late</Badge> : <span className="text-slate-300">—</span>),
        },
      ]}
    />
  );
}

/** Shared by both the plain-teacher view and the owner's "My Courses"
 * section within the admin view — same data shape, same sections. */
function TeacherSections({ data, isLoading }: { data: LmsTeacherDashboard | undefined; isLoading: boolean }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 font-semibold text-slate-900">My Courses</h3>
        <MyCoursesTable courses={data?.myCourses ?? []} isLoading={isLoading} />
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-slate-900">Pending Grading</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            to="/lms/courses"
            icon={ClipboardList}
            label="Assignment Submissions"
            value={data?.pendingGrading.assignmentSubmissions ?? 0}
            loading={isLoading}
            tone="amber"
          />
          <StatCard
            to="/lms/courses"
            icon={FileQuestion}
            label="Short-Answer Questions"
            value={data?.pendingGrading.shortAnswerQuestions ?? 0}
            loading={isLoading}
            tone="purple"
          />
        </div>
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-slate-900">Recent Submissions</h3>
        <RecentSubmissionsList items={data?.recentSubmissions ?? []} isLoading={isLoading} />
      </div>
    </div>
  );
}

/** Exported for reuse on the LMS-only tenant's main /dashboard landing
 * (DashboardPage.tsx) — same component, same query, not duplicated. */
export function TeacherDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['lms-dashboard-teacher'],
    queryFn: LmsDashboardApi.teacher,
  });

  return <TeacherSections data={data} isLoading={isLoading} />;
}

const ACTIVITY_ICON: Record<LmsAdminDashboardActivity['type'], typeof UserPlus> = {
  enrollment: UserPlus,
  completion: CheckCircle2,
  submission: FileText,
};

function activityLabel(a: LmsAdminDashboardActivity): string {
  const student = a.studentName ?? 'A student';
  const course = a.courseTitle ?? 'a course';
  switch (a.type) {
    case 'enrollment':
      return `${student} enrolled in ${course}`;
    case 'completion':
      return `${student} completed ${course}`;
    case 'submission':
      return `${student} submitted "${a.assignmentTitle ?? 'an assignment'}" in ${course}`;
  }
}

/** Exported for reuse on the LMS-only tenant's main /dashboard landing
 * (DashboardPage.tsx) — same component, same queries, not duplicated. */
export function AdminDashboard() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const { data, isLoading } = useQuery({
    queryKey: ['lms-dashboard-admin', page, limit],
    queryFn: () => LmsDashboardApi.admin({ page, limit }),
  });
  // Owner is both admin-role and teaching-role — the backend's
  // teacherDashboard scopes myCourses to courses THIS user authored, which
  // may be empty for an owner who never personally created one. Fetch it
  // unconditionally (cheap, same pattern as other dashboards) and only
  // render the personal section if it actually has content — admin data
  // stays primary either way.
  const { data: teacherData, isLoading: teacherLoading } = useQuery({
    queryKey: ['lms-dashboard-teacher'],
    queryFn: LmsDashboardApi.teacher,
  });

  const totals = data?.totals;
  const perCourse = data?.perCourse;
  const recentActivity = data?.recentActivity ?? [];
  const totalCourses = totals ? totals.courses.draft + totals.courses.published + totals.courses.archived : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard
          to="/lms/courses"
          icon={BookMarked}
          label="Courses"
          value={totalCourses}
          subline={
            totals
              ? `${totals.courses.published} published · ${totals.courses.draft} draft · ${totals.courses.archived} archived`
              : undefined
          }
          loading={isLoading}
          tone="blue"
        />
        <StatCard
          to="/lms/courses"
          icon={Users}
          label="Active Enrollments"
          value={totals?.activeEnrollments ?? 0}
          loading={isLoading}
          tone="indigo"
        />
        <StatCard
          to="/lms/courses"
          icon={GraduationCap}
          label="Students Enrolled"
          value={totals?.studentsEnrolled ?? 0}
          loading={isLoading}
          tone="purple"
        />
        <StatCard
          to="/lms/courses"
          icon={CheckCircle2}
          label="Completed Enrollments"
          value={totals?.completedEnrollments ?? 0}
          loading={isLoading}
          tone="green"
        />
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-slate-900">Per-Course</h3>
        <DataTable
          rows={perCourse?.items ?? []}
          getRowId={(c) => c.id}
          isLoading={isLoading}
          emptyMessage="No courses yet."
          columns={[
            {
              key: 'title',
              header: 'Title',
              render: (c) => (
                <Link
                  to={`/lms/courses/${c.id}`}
                  className="font-medium text-slate-900 hover:text-brand-700 hover:underline"
                >
                  {c.title}
                </Link>
              ),
            },
            { key: 'status', header: 'Status', render: (c) => <Badge tone={statusTone[c.status]}>{c.status}</Badge> },
            { key: 'enrolledCount', header: 'Enrolled', render: (c) => c.enrolledCount },
            { key: 'avgProgress', header: 'Avg. Progress', render: (c) => `${c.avgProgress}%` },
            { key: 'completionRate', header: 'Completion Rate', render: (c) => `${c.completionRate}%` },
          ]}
        />
        {perCourse && (
          <Pagination
            page={perCourse.page}
            totalPages={perCourse.totalPages}
            total={perCourse.total}
            limit={perCourse.limit}
            onPageChange={setPage}
            onLimitChange={(l) => {
              setPage(1);
              setLimit(l);
            }}
          />
        )}
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-slate-900">Recent Activity</h3>
        {isLoading ? (
          <div className="card p-8 text-center text-slate-400">Loading…</div>
        ) : recentActivity.length === 0 ? (
          <div className="card p-8 text-center text-slate-400">No recent activity.</div>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {recentActivity.map((a, i) => {
              const Icon = ACTIVITY_ICON[a.type];
              return (
                <li key={i} className="flex items-center gap-3 px-4 py-3 text-sm">
                  <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="flex-1 text-slate-700">{activityLabel(a)}</span>
                  <span className="shrink-0 text-xs text-slate-400">{formatRelative(a.at)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {(teacherData?.myCourses.length ?? 0) > 0 && (
        <div className="border-t border-slate-200 pt-6">
          <p className="mb-4 text-sm text-slate-500">
            Courses you personally authored, shown the same way a teacher would see them.
          </p>
          <TeacherSections data={teacherData} isLoading={teacherLoading} />
        </div>
      )}
    </div>
  );
}
